import { z } from "zod";
import { oc } from "@orpc/contract";
import { IntegrationBaseSchema, IntegrationKey } from "./base";

/**
 * Checkbook bank account status
 */
export const CheckbookBankStatusSchema = z.enum([
  "PENDING",
  "VERIFIED",
  "DEPOSIT_ONLY",
]);

export type CheckbookBankStatus = z.infer<typeof CheckbookBankStatusSchema>;

/**
 * Bank account type
 */
export const BankAccountTypeSchema = z.enum([
  "CHECKING",
  "SAVINGS",
  "BUSINESS",
]);

export type BankAccountType = z.infer<typeof BankAccountTypeSchema>;

/**
 * Payment Account Schema - A bank account linked for payments
 * Accounts may be manually entered or legacy Plaid-linked,
 * and are verified via Checkbook micro-deposits.
 */
export const PaymentAccountSchema = z.object({
  id: z.uuid(), // Unique ID for this payment account
  name: z.string(), // User-provided name for this account
  source: z.enum(["manual", "plaid"]).optional(),
  // Plaid linkage for legacy accounts
  plaidAccountId: z.string().optional(),
  plaidBankAccountId: z.string().optional(),
  institutionName: z.string(), // Bank name for display
  accountMask: z.string(), // Last 2-4 digits for display
  // Checkbook details
  checkbookBankId: z.string().optional(), // Checkbook bank account ID after registration
  checkbookStatus: CheckbookBankStatusSchema.optional(), // Verification status
  // Bank account details (user-entered, encrypted in storage)
  routingNumber: z.string(), // User-entered routing number
  accountNumber: z.string(), // User-entered account number (obfuscated on frontend)
  accountType: BankAccountTypeSchema,
  // Micro-deposit tracking
  microDepositsReleasedAt: z.coerce.date().optional(), // When micro-deposits were initiated
  microDepositAmount1: z.number().optional(), // Expected amount 1 (from Checkbook, for auto-verify)
  microDepositAmount2: z.number().optional(), // Expected amount 2 (from Checkbook, for auto-verify)
  verifiedAt: z.coerce.date().optional(), // When the account was verified
  verifiedBy: z.uuid().optional(), // User who verified the account
  // Metadata
  createdAt: z.coerce.date(),
  createdBy: z.uuid(), // User who added this account
  isDefault: z.boolean().prefault(false), // Default account for payments
  // Auto-verification tracking (for rate limiting)
  failedVerificationAttempts: z.number().prefault(0), // Count of failed auto-verification attempts
  lastVerificationAttemptAt: z.coerce.date().optional(), // Last auto-verification attempt timestamp
});

export type PaymentAccount = z.infer<typeof PaymentAccountSchema>;

/**
 * Checkbook Marketplace User credentials
 * Stored within PaymentsIntegrationSettings for each organization
 */
export const CheckbookMarketplaceUserSchema = z.object({
  // Core Checkbook identifiers
  checkbookSystemId: z.string(), // Checkbook's internal ID
  checkbookUserId: z.string(), // Our user_id (organization ID)

  // API credentials (encrypted in storage)
  checkbookApiKey: z.string(), // Publishable key
  checkbookApiSecret: z.string(), // Secret key

  // Status
  status: z.enum(["ACTIVE", "PENDING_KYC", "SUSPENDED"]).prefault("ACTIVE"),

  // Metadata
  createdAt: z.coerce.date(),
  createdBy: z.uuid(),

  // Terms & Conditions acceptance
  termsAcceptedAt: z.coerce.date().optional(),
  termsAcceptedBy: z.uuid().optional(),
  termsAcceptedByName: z.string().optional(),
  termsAcceptedByEmail: z.string().optional(),
  termsAcceptedFromIp: z.string().optional(),

  // Signature (required for payments > $2000)
  signatureUploadedAt: z.coerce.date().optional(),
  signatureUploadedBy: z.uuid().optional(),
});

export type CheckbookMarketplaceUser = z.infer<
  typeof CheckbookMarketplaceUserSchema
>;

/**
 * Payments Integration Settings Schema
 */
export const PaymentsIntegrationSettingsSchema = z.object({
  accounts: z.array(PaymentAccountSchema).prefault([]),
  // Checkbook marketplace user credentials (created on first bank account link)
  checkbookUser: CheckbookMarketplaceUserSchema.optional(),
});

export type PaymentsIntegrationSettings = z.infer<
  typeof PaymentsIntegrationSettingsSchema
>;

/**
 * Payments integration variant (without base fields)
 */
export const PaymentsIntegrationSettingsSchemaWithoutBase = z.object({
  key: z.literal(IntegrationKey.Payments),
  settings: PaymentsIntegrationSettingsSchema,
});

/**
 * Full Payments integration schema (with base fields)
 */
export const PaymentsIntegrationSchema = IntegrationBaseSchema.merge(
  PaymentsIntegrationSettingsSchemaWithoutBase,
);

/**
 * Type guard to check if an integration is a Payments integration
 */
export function isPaymentsIntegration(
  integration: { key: string; settings?: unknown } | null | undefined,
): integration is PaymentsIntegration {
  return integration?.key === IntegrationKey.Payments;
}

/**
 * Type guard to check if settings has valid Checkbook marketplace user credentials
 */
export function hasCheckbookCredentials(
  settings: PaymentsIntegrationSettings | null | undefined,
): settings is PaymentsIntegrationSettings & {
  checkbookUser: CheckbookMarketplaceUser;
} {
  return (
    settings?.checkbookUser != null &&
    typeof settings.checkbookUser.checkbookApiKey === "string" &&
    typeof settings.checkbookUser.checkbookApiSecret === "string"
  );
}

export type PaymentsIntegration = z.infer<typeof PaymentsIntegrationSchema>;

// ============================================================================
// KYB/KYC Schemas (for Checkbook onboarding)
// These fields are passed to Checkbook but NOT stored in our database
// ============================================================================

/**
 * US State codes for address validation
 */
export const USStateSchema = z.enum([
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
  "DC",
]);

export type USState = z.infer<typeof USStateSchema>;

/**
 * US States with display labels for form dropdowns
 */
export const US_STATES: readonly { value: USState; label: string }[] = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "DC", label: "District of Columbia" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
] as const;

/**
 * Industry codes supported by Checkbook (NAICS sectors)
 */
export const IndustryCodeSchema = z.enum([
  "11", // Agriculture
  "21", // Mining
  "22", // Utilities
  "23", // Construction
  "33", // Manufacturing
  "42", // Wholesale Trade
  "45", // Retail Trade
  "49", // Transportation
  "51", // Information
  "52", // Finance and Insurance
  "53", // Real Estate
  "54", // Professional Services
  "55", // Management
  "56", // Administrative Services
  "61", // Educational Services
  "62", // Health Care
  "71", // Arts and Entertainment
  "72", // Accommodation and Food Services
  "81", // Other Services
  "92", // Public Administration
]);

export type IndustryCode = z.infer<typeof IndustryCodeSchema>;

/**
 * Industry codes with display labels for form dropdowns
 */
export const INDUSTRY_CODES: readonly { value: IndustryCode; label: string }[] =
  [
    { value: "11", label: "Agriculture, Forestry, Fishing and Hunting" },
    { value: "21", label: "Mining, Quarrying, and Oil and Gas Extraction" },
    { value: "22", label: "Utilities" },
    { value: "23", label: "Construction" },
    { value: "33", label: "Manufacturing" },
    { value: "42", label: "Wholesale Trade" },
    { value: "45", label: "Retail Trade" },
    { value: "49", label: "Transportation and Warehousing" },
    { value: "51", label: "Information" },
    { value: "52", label: "Finance and Insurance" },
    { value: "53", label: "Real Estate and Rental and Leasing" },
    { value: "54", label: "Professional, Scientific, and Technical Services" },
    { value: "55", label: "Management of Companies and Enterprises" },
    { value: "56", label: "Administrative and Support Services" },
    { value: "61", label: "Educational Services" },
    { value: "62", label: "Health Care and Social Assistance" },
    { value: "71", label: "Arts, Entertainment, and Recreation" },
    { value: "72", label: "Accommodation and Food Services" },
    { value: "81", label: "Other Services (except Public Administration)" },
    { value: "92", label: "Public Administration" },
  ] as const;

/**
 * Citizenship status for principal officer
 */
export const CitizenshipStatusSchema = z.enum([
  "us_citizen",
  "resident",
  "non_resident",
]);

export type CitizenshipStatus = z.infer<typeof CitizenshipStatusSchema>;

/**
 * Citizenship status options with display labels for form dropdowns
 */
export const CITIZENSHIP_OPTIONS: readonly {
  value: CitizenshipStatus;
  label: string;
}[] = [
  { value: "us_citizen", label: "U.S. Citizen" },
  { value: "resident", label: "Permanent Resident" },
  { value: "non_resident", label: "Non-Resident" },
] as const;

/**
 * Address schema for business and principal officer
 */
export const AddressSchema = z.object({
  line1: z.string().min(1, "Street address is required"),
  line2: z.string().optional(),
  city: z.string().min(1, "City is required"),
  state: USStateSchema,
  zip: z.string().regex(/^\d{5}(-\d{4})?$/, "Invalid ZIP code format"),
});

export type Address = z.infer<typeof AddressSchema>;

/**
 * Business information for KYB (Know Your Business)
 * Required when first setting up payments
 */
export const BusinessInfoSchema = z.object({
  // Legal business name (required)
  legalBusinessName: z.string().min(1, "Legal business name is required"),
  // Business details
  website: z
    .string()
    .transform((v) => {
      if (!v) return v;
      // Prepend https:// if no protocol is present
      if (!/^https?:\/\//i.test(v)) return `https://${v}`;
      return v;
    })
    .pipe(z.string().url())
    .optional()
    .or(z.literal("")),
  taxId: z
    .string()
    .regex(/^\d{2}-?\d{7}$/, "Tax ID must be in format XX-XXXXXXX"),
  businessPhone: z.string().optional(),
  industry: IndustryCodeSchema,
  // Incorporation date (MM/DD/YYYY or YYYY-MM-DD)
  incorporationDate: z.string().min(1, "Incorporation date is required"),
  // Business address
  address: AddressSchema,
});

export type BusinessInfo = z.infer<typeof BusinessInfoSchema>;

/**
 * Principal officer information for KYC (Know Your Customer)
 * This is the financial officer or authorized signer
 * SENSITIVE: This data is passed to Checkbook but NOT stored in our database
 */
export const PrincipalOfficerInfoSchema = z.object({
  // Name
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  // SSN (sensitive - not stored)
  ssn: z.string().regex(/^\d{3}-?\d{2}-?\d{4}$/, "SSN must be 9 digits"),
  // Date of birth (MM/DD/YYYY or YYYY-MM-DD)
  dateOfBirth: z.string().min(1, "Date of birth is required"),
  // Employment
  occupation: z.string().min(1, "Occupation is required"),
  citizenshipStatus: CitizenshipStatusSchema,
  // Principal officer address
  address: AddressSchema,
});

export type PrincipalOfficerInfo = z.infer<typeof PrincipalOfficerInfoSchema>;

/**
 * Combined KYB/KYC information for Checkbook onboarding
 * Required when creating the first payment account (marketplace user setup)
 */
export const CheckbookOnboardingInfoSchema = z.object({
  business: BusinessInfoSchema,
  principalOfficer: PrincipalOfficerInfoSchema,
});

export type CheckbookOnboardingInfo = z.infer<
  typeof CheckbookOnboardingInfoSchema
>;

// ============================================================================
// Contracts
// ============================================================================

/**
 * Add a payment account and initiate Checkbook verification
 *
 * When this is the FIRST payment account (no existing Checkbook marketplace user),
 * the `onboardingInfo` field is REQUIRED to set up the organization with Checkbook.
 * This includes business information (KYB) and principal officer information (KYC).
 *
 * IMPORTANT: The sensitive data in onboardingInfo (SSN, DOB, etc.) is passed directly
 * to Checkbook and is NOT stored in our database.
 */
const addPaymentAccount = oc
  .input(
    z.object({
      name: z.string().min(1, "Account name is required"),
      institutionName: z.string().min(1, "Institution name is required"),
      accountMask: z.string().min(2).max(4).optional(),
      routingNumber: z.string().length(9, "Routing number must be 9 digits"),
      accountNumber: z.string().min(4, "Account number is required"),
      accountType: BankAccountTypeSchema,
      isDefault: z.boolean().optional(),
      // KYB/KYC info - required for first payment account only
      // This data is passed to Checkbook but NOT stored in our database
      onboardingInfo: CheckbookOnboardingInfoSchema.optional(),
      // Must be true when onboardingInfo is present (T&C acceptance required)
      termsAccepted: z.boolean().optional(),
    }),
  )
  .output(PaymentsIntegrationSchema)
  .route({ method: "POST" });

/**
 * Verify micro-deposits for a payment account
 */
const verifyMicroDeposits = oc
  .input(
    z.object({
      paymentAccountId: z.uuid(),
      amount1: z.number().positive().max(1, "Amount must be less than $1.00"),
      amount2: z.number().positive().max(1, "Amount must be less than $1.00"),
    }),
  )
  .output(PaymentsIntegrationSchema)
  .route({ method: "POST" });

/**
 * Resend micro-deposits if verification fails or they expire
 */
const resendMicroDeposits = oc
  .input(
    z.object({
      paymentAccountId: z.uuid(),
    }),
  )
  .output(PaymentsIntegrationSchema)
  .route({ method: "POST" });

/**
 * Delete a payment account
 */
const deletePaymentAccount = oc
  .input(
    z.object({
      paymentAccountId: z.uuid(),
    }),
  )
  .output(PaymentsIntegrationSchema)
  .route({ method: "DELETE" });

/**
 * Set default payment account
 */
const setDefaultPaymentAccount = oc
  .input(
    z.object({
      paymentAccountId: z.uuid(),
    }),
  )
  .output(PaymentsIntegrationSchema)
  .route({ method: "POST" });

/**
 * Update business/KYB information for an existing Checkbook marketplace user
 *
 * IMPORTANT: Sensitive fields (SSN, DOB) are optional for updates.
 * If not provided, they will not be changed in Checkbook.
 * This data is passed directly to Checkbook and NOT stored in our database.
 */
const updateBusinessInfo = oc
  .input(
    z.object({
      business: z.object({
        legalBusinessName: z.string().min(1, "Legal business name is required"),
        website: z
          .string()
          .transform((v) => {
            if (!v) return v;
            if (!/^https?:\/\//i.test(v)) return `https://${v}`;
            return v;
          })
          .pipe(z.string().url())
          .optional()
          .or(z.literal("")),
        taxId: z
          .string()
          .regex(/^\d{2}-?\d{7}$/, "Tax ID must be in format XX-XXXXXXX"),
        businessPhone: z.string().optional(),
        industry: IndustryCodeSchema,
        incorporationDate: z.string().min(1, "Incorporation date is required"),
        address: AddressSchema,
      }),
      principalOfficer: z.object({
        firstName: z.string().min(1, "First name is required"),
        lastName: z.string().min(1, "Last name is required"),
        // SSN is optional for updates - only send if user wants to change it
        ssn: z
          .string()
          .regex(/^\d{3}-?\d{2}-?\d{4}$/, "SSN must be 9 digits")
          .optional(),
        // DOB is optional for updates - only send if user wants to change it
        dateOfBirth: z.string().optional(),
        occupation: z.string().min(1, "Occupation is required"),
        citizenshipStatus: CitizenshipStatusSchema,
        address: AddressSchema,
      }),
    }),
  )
  .output(z.object({ success: z.boolean() }))
  .route({ method: "POST" });

/**
 * Upload a signature image for the Checkbook marketplace user.
 * Required for sending payments over $2,000.
 */
const uploadSignature = oc
  .input(
    z.object({
      /** Base64-encoded image (png/jpeg). Max ~2 MB raw file. */
      signatureBase64: z.string().min(1, "Signature image is required"),
    }),
  )
  .output(z.object({ success: z.boolean() }))
  .route({ method: "POST" });

/**
 * Payments Integration Contracts
 * Note: Named "paymentsIntegrationContracts" to avoid conflict with finance/payments
 */
export const paymentsIntegrationContracts = {
  addPaymentAccount,
  verifyMicroDeposits,
  resendMicroDeposits,
  deletePaymentAccount,
  setDefaultPaymentAccount,
  updateBusinessInfo,
  uploadSignature,
} as const;

export type PaymentsIntegrationContracts = typeof paymentsIntegrationContracts;
